import React from 'react';
import { useBranding } from '../../context/BrandingContext';

/**
 * The copyright notice: the client's own text, or one composed from its legal or
 * product name (the product's when no client is known).
 */
export function useCopyrightNotice(): string | null {
  const { branding } = useBranding();
  // A legal name is often stored as "Client College Inc.", which would otherwise
  // render as "Inc.. All rights reserved."
  const owner = (branding.legalName || branding.productName || '').replace(/[.,;:\s]+$/, '');
  return branding.copyrightText || (owner ? `© ${new Date().getFullYear()} ${owner}. All rights reserved.` : null);
}

/**
 * Terms, privacy and support links: the client's where set, else the product's.
 * Renders nothing when neither has any.
 */
export const BrandLinks: React.FC<{ className?: string; linkClassName?: string; separator?: React.ReactNode }> = ({
  className = '',
  linkClassName = 'hover:underline',
  separator = <span aria-hidden="true">|</span>,
}) => {
  const { branding } = useBranding();

  const links = [
    branding.privacyUrl && { label: 'Privacy', href: branding.privacyUrl },
    branding.termsUrl && { label: 'Terms', href: branding.termsUrl },
    branding.supportUrl && { label: 'Support', href: branding.supportUrl },
  ].filter(Boolean) as Array<{ label: string; href: string }>;

  if (!links.length) return null;

  return (
    <div className={`flex flex-wrap items-center justify-center gap-4 ${className}`}>
      {links.map((link, i) => (
        <React.Fragment key={link.label}>
          {i > 0 && separator}
          <a href={link.href} target="_blank" rel="noopener noreferrer" className={linkClassName}>
            {link.label}
          </a>
        </React.Fragment>
      ))}
    </div>
  );
};

/** "Powered by <product>", from the frontend env. Nothing when it is not set. */
export const PoweredBy: React.FC<{ className?: string; style?: React.CSSProperties }> = ({ className = '', style }) => {
  const { branding } = useBranding();
  if (!branding.poweredBy) return null;
  return (
    <p className={className} style={style}>
      Powered by {branding.poweredBy}
    </p>
  );
};

/** Footer of the sign-in pages: copyright, legal links and the product credit. */
export const BrandFooter: React.FC<{ className?: string }> = ({ className = '' }) => {
  const notice = useCopyrightNotice();

  return (
    <div className={`mt-6 space-y-1 text-center text-xs text-gray-400 ${className}`}>
      {notice && <div>{notice}</div>}
      <BrandLinks />
      <PoweredBy />
    </div>
  );
};
